import { useLocation, Navigate, Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { MapPin, TrendingUp, Users, CheckCircle, Phone } from 'lucide-react'
import { trackEvent } from '../analytics'
import { getCityBySlug, cityPages } from '../data/cityPages'
import { FaqSection } from '../components/faq/FaqSection'

const services = [
  { name: 'Social Media Marketing', path: '/services/social-media-marketing', desc: 'Brand presence and content strategy' },
  { name: 'Meta Ads & Paid Ads', path: '/services/paid-ads', desc: 'Qualified lead generation campaigns' },
  { name: 'SEO', path: '/services/seo', desc: 'Organic search visibility' },
  { name: 'Lead Generation', path: '/services/lead-generation', desc: 'Structured pipelines for your business' },
  { name: 'Website Development', path: '/services/website-development', desc: 'Conversion-focused, fast websites' },
  { name: 'Funnel Building', path: '/services/funnel-building', desc: 'Awareness to decision funnels' },
]

const highlights = [
  { icon: TrendingUp, title: 'BLS Method', desc: 'Brand → Lead → Sales. A complete marketing system.' },
  { icon: Users, title: 'Niche Focus', desc: 'B2B, Real Estate & Marble. Not generic marketing.' },
  { icon: CheckCircle, title: 'Measurable ROI', desc: 'Qualified leads, not just clicks and impressions.' },
]

const processSteps = [
  { step: '01', title: 'Audit & Strategy', desc: 'We review your current marketing, competitors, and goals, then map a clear plan built around your buyers.' },
  { step: '02', title: 'Brand Foundation', desc: 'We sharpen your positioning and content so prospects trust your business before they even enquire.' },
  { step: '03', title: 'Lead Generation', desc: 'Targeted Meta Ads, SEO, and landing pages bring in qualified enquiries — not just clicks.' },
  { step: '04', title: 'Sales & Optimization', desc: 'Follow-up systems and funnels convert leads, while we optimize every month for better ROI.' },
]

const deliverables = [
  'Dedicated strategy and account management',
  'Meta Ads (Facebook & Instagram) campaign setup and management',
  'SEO: on-page optimization, keywords, and content',
  'Conversion-focused landing pages and funnels',
  'Monthly performance reports with real metrics',
  'Lead tracking and follow-up systems',
]

export default function CityPage() {
  const location = useLocation()
  const citySlug = location.pathname.replace(/^\//, '').replace(/\/$/, '')
  const city = getCityBySlug(citySlug)

  if (!city) return <Navigate to="/404" replace />

  const localBusinessSchema = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: 'DigiSutra',
    description: city.metaDescription,
    url: `https://digisutra.in/${city.slug}`,
    telephone: '+91-8200670752',
    email: 'info@digisutra.in',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '812-814, Western Business Park, Opp. SD Jain School, Vesu',
      addressLocality: 'Surat',
      addressRegion: 'Gujarat',
      postalCode: '395007',
      addressCountry: 'IN',
    },
    areaServed: {
      '@type': 'City',
      name: city.cityName,
    },
    serviceArea: { '@type': 'State', name: city.state },
  }

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(localBusinessSchema) }} />

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
        {/* Hero Section */}
        <section className="min-h-[60vh] flex flex-col justify-center px-4 pt-28 pb-16 bg-white dark:bg-navy-900">
          <div className="container-wide max-w-4xl">
            <nav className="text-sm text-slate-500 dark:text-slate-400 mb-6 flex items-center gap-2">
              <Link to="/" className="hover:text-primary dark:hover:text-accent transition-colors">Home</Link>
              <span>›</span>
              <span className="text-slate-700 dark:text-slate-200">Digital Marketing in {city.cityName}</span>
            </nav>

            <div className="flex items-center gap-2 mb-4">
              <MapPin size={18} className="text-primary dark:text-accent" />
              <span className="text-primary dark:text-accent font-semibold text-sm uppercase tracking-wide">
                Serving {city.cityName}, {city.state}
              </span>
            </div>

            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-slate-800 dark:text-white mb-6 leading-tight">
              {city.h1}
            </h1>
            <p className="text-lg sm:text-xl text-slate-600 dark:text-slate-300 max-w-3xl mb-8 leading-relaxed">
              {city.intro}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-4 rounded-2xl bg-accent text-navy-900 font-semibold text-lg hover:opacity-90 transition-opacity shadow-lg"
                onClick={() => trackEvent('cta_click', { cta_name: 'book_strategy_call', from: `city_${city.slug}` })}
              >
                Book Strategy Call
              </Link>
              <a
                href="tel:+918200670752"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-2xl border border-primary/30 dark:border-accent/30 text-primary dark:text-accent font-semibold text-lg hover:bg-primary/5 dark:hover:bg-accent/5 transition-colors"
                onClick={() => trackEvent('cta_click', { cta_name: 'call_now', from: `city_${city.slug}` })}
              >
                <Phone size={18} /> Call Now
              </a>
            </div>
          </div>
        </section>

        {/* Why Us */}
        <section className="py-16 px-4 bg-slate-50 dark:bg-navy-900/50">
          <div className="container-wide max-w-4xl">
            <h2 className="text-3xl font-bold text-slate-800 dark:text-white mb-6">
              Why DigiSutra for {city.cityName} Businesses?
            </h2>
            <p className="text-slate-600 dark:text-slate-300 text-lg leading-relaxed mb-8">{city.whyUs}</p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {highlights.map(({ icon: Icon, title, desc }) => (
                <div key={title} className="bg-white dark:bg-navy-800 rounded-2xl p-6 border border-slate-200/60 dark:border-white/5">
                  <Icon size={28} className="text-primary dark:text-accent mb-3" />
                  <h3 className="font-semibold text-slate-800 dark:text-white mb-2">{title}</h3>
                  <p className="text-slate-600 dark:text-slate-400 text-sm">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Industries */}
        <section className="py-16 px-4 bg-white dark:bg-navy-900">
          <div className="container-wide max-w-4xl">
            <h2 className="text-3xl font-bold text-slate-800 dark:text-white mb-4">
              Industries We Serve in {city.cityName}
            </h2>
            <div className="flex flex-wrap gap-3 mt-6">
              {city.industries.map((ind) => (
                <span key={ind} className="px-4 py-2 bg-primary/5 dark:bg-accent/5 text-primary dark:text-accent border border-primary/20 dark:border-accent/20 rounded-full text-sm font-medium">
                  {ind}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* How We Help */}
        <section className="py-16 px-4 bg-slate-50 dark:bg-navy-900/50">
          <div className="container-wide max-w-4xl">
            <h2 className="text-3xl font-bold text-slate-800 dark:text-white mb-4">
              How We Help {city.cityName} Businesses Grow
            </h2>
            <p className="text-slate-600 dark:text-slate-300 mb-8">
              A structured, step-by-step system designed for {city.cityName}'s {city.state} market — built to generate qualified leads and predictable growth.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {processSteps.map((p) => (
                <div key={p.step} className="bg-white dark:bg-navy-800 rounded-2xl p-6 border border-slate-200/60 dark:border-white/5">
                  <span className="text-2xl font-bold text-primary/40 dark:text-accent/40">{p.step}</span>
                  <h3 className="font-semibold text-slate-800 dark:text-white mt-2 mb-2">{p.title}</h3>
                  <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed">{p.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* What's Included */}
        <section className="py-16 px-4 bg-white dark:bg-navy-900">
          <div className="container-wide max-w-4xl">
            <h2 className="text-3xl font-bold text-slate-800 dark:text-white mb-6">
              What's Included for {city.cityName} Clients
            </h2>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
              {deliverables.map((d) => (
                <li key={d} className="flex items-start gap-3 text-slate-600 dark:text-slate-300">
                  <CheckCircle size={20} className="text-primary dark:text-accent shrink-0 mt-0.5" />
                  <span>{d}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Services */}
        <section className="py-16 px-4 bg-slate-50 dark:bg-navy-900/50">
          <div className="container-wide max-w-4xl">
            <h2 className="text-3xl font-bold text-slate-800 dark:text-white mb-2">
              Digital Marketing Services in {city.cityName}
            </h2>
            <p className="text-slate-600 dark:text-slate-300 mb-8">
              Full-service digital marketing for {city.cityName} businesses — from brand awareness to lead generation.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {services.map((s) => (
                <Link key={s.path} to={s.path} className="block bg-white dark:bg-navy-800 rounded-2xl p-5 border border-slate-200/60 dark:border-white/5 hover:border-primary/40 dark:hover:border-accent/40 transition-colors group">
                  <h3 className="font-semibold text-slate-800 dark:text-white mb-1 group-hover:text-primary dark:group-hover:text-accent transition-colors">{s.name}</h3>
                  <p className="text-slate-500 dark:text-slate-400 text-sm">{s.desc}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ — CMS location key = city slug (local SEO / AEO) */}
        <section className="py-16 px-4 bg-white dark:bg-navy-900">
          <div className="container-wide">
            <FaqSection
              location={city.slug}
              title={`Frequently Asked Questions — Digital Marketing in ${city.cityName}`}
              subtitle={`Local answers for businesses searching for a digital marketing agency in ${city.cityName}.`}
              fallback={city.faq}
              schemaId={`city-faq-${city.slug}`}
            />
          </div>
        </section>

        {/* Nearby */}
        <section className="py-10 px-4 bg-slate-50 dark:bg-navy-900/50">
          <div className="container-wide max-w-4xl">
            <p className="text-slate-500 dark:text-slate-400 text-sm">{city.nearbyContext}</p>
          </div>
        </section>

        {/* Other Cities */}
        <section className="py-16 px-4 bg-white dark:bg-navy-900 border-t border-slate-200/60 dark:border-white/10">
          <div className="container-wide max-w-4xl">
            <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-6">We Also Serve</h2>
            <div className="flex flex-wrap gap-3">
              {cityPages.filter((c) => c.slug !== city.slug).map((c) => (
                <Link
                  key={c.slug}
                  to={`/${c.slug}`}
                  className="px-4 py-2 text-sm border border-slate-200/70 dark:border-white/10 rounded-full text-slate-600 dark:text-slate-300 hover:border-primary/40 dark:hover:border-accent/40 hover:text-primary dark:hover:text-accent transition-colors"
                >
                  {c.cityName}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 px-4 bg-primary dark:bg-accent/10 text-white text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Grow Your {city.cityName} Business?</h2>
          <p className="text-white/80 dark:text-slate-300 mb-8 max-w-xl mx-auto">
            Book a free 30-minute strategy call. We'll audit your current marketing and show you exactly where you can grow.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center justify-center px-8 py-4 rounded-2xl bg-white dark:bg-accent text-primary dark:text-navy-900 font-semibold text-lg hover:opacity-90 transition-opacity shadow-lg"
            onClick={() => trackEvent('cta_click', { cta_name: 'book_strategy_call', from: `city_${city.slug}_footer` })}
          >
            Book Free Strategy Call
          </Link>
        </section>
      </motion.div>
    </>
  )
}
